import { Injectable } from '@angular/core';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { IRealtimeService } from '../interfaces/realtime-service.interface';
import { SessionService } from '../app-state-services/session/session.service';
import { RuntimeConfigService } from '../../../common/services/runtime-config/runtime-config.service';

@Injectable({
  providedIn: 'root',
})
export class RSocketService implements IRealtimeService {
  private stompClient!: Client;
  private messageSubject = new BehaviorSubject<any>(null);
  public messages$: Observable<any> = this.messageSubject.asObservable();

  /**
   * Per-conversation STOMP subscriptions keyed by conversationContextId.
   * Each entry holds the message-topic and state-topic subscriptions so they
   * can be unsubscribed cleanly when the conversation is left.
   */
  private conversationSubscriptions = new Map<string, StompSubscription[]>();

  /** Conversations the widget wants subscribed — replayed in onConnect after a (re)connect. */
  private desiredConversations = new Set<string>();

  constructor(
    private sessionService: SessionService,
    private runtimeConfigService: RuntimeConfigService
  ) {
    this.runtimeConfigService.config$.subscribe((cfg) => {
      if (cfg.humanAgentFeature && !environment.useSignalR) {
        this.connect();
      }
    });
  }

  connect(): void {
    if (this.stompClient?.active) {
      return;
    }
    this.stompClient = new Client({
      webSocketFactory: () => new SockJS(environment.rSocketUrl),
      debug: (str) => console.log(str),
      reconnectDelay: 5000,
      // OD3: present the dealer (groups.code) on the STOMP CONNECT so the backend binds the session's
      // dealer and authorizes per-conversation SUBSCRIBEs. Re-read per (re)connect — the dealer may be
      // resolved after this service is constructed; the client retries until it's available.
      beforeConnect: () => {
        const tenantCode = this.sessionService.getTenantCode();
        const workshopId = this.sessionService.getWorkshopId();
        const headers: Record<string, string> = {};
        if (tenantCode) headers['X-Tenant-Code'] = tenantCode;
        if (workshopId) headers['X-Workshop-Id'] = workshopId;
        this.stompClient.connectHeaders = headers;
      },
      onConnect: () => {
        console.log('[STOMP] Connected');
        // No global /chat/response subscription — backend now publishes per-conversation.
        // (Re)subscribe everything the widget asked for: covers reconnects and any join that
        // happened before the connection was up.
        this.desiredConversations.forEach((id) => this.subscribeToConversation(id));
      },
      onStompError: (frame) => {
        console.error('[STOMP] Broker error:', frame.headers['message']);
        console.error('[STOMP] Details:', frame.body);
      },
      onWebSocketClose: (event) => {
        console.warn('[STOMP] WebSocket closed', event);
        // Handles are dead after a close; drop them so onConnect re-subscribes cleanly.
        this.conversationSubscriptions.clear();
      }
    });
    this.stompClient.activate();
  }

  sendMessage(message: any): void {
    if (this.stompClient && this.stompClient.connected) {
      this.stompClient.publish({
        destination: '/app/chat.message',
        body: JSON.stringify(message)
      });
    }
  }

  disconnect(): void {
    // Unsubscribe all active conversation topics before deactivating
    this.conversationSubscriptions.forEach((subs) => {
      subs.forEach((s) => { try { s.unsubscribe(); } catch { /* ignore */ } });
    });
    this.conversationSubscriptions.clear();
    this.desiredConversations.clear();

    if (this.stompClient && this.stompClient.active) {
      this.stompClient.deactivate();
    }
  }

  isConnected(): boolean {
    return this.stompClient && this.stompClient.connected;
  }

  /**
   * Subscribe to per-conversation STOMP topics:
   *   /topic/conversation/{id}        — live messages
   *   /topic/conversation/{id}/state  — conversation state events
   *
   * If a subscription already exists for this id it is a no-op (idempotent). Joining while the
   * socket is down is remembered and applied on the next connect.
   */
  joinConversation(conversationContextId: string): void {
    if (!conversationContextId) {
      console.warn('[STOMP] joinConversation called with empty id — skipping');
      return;
    }

    // Record intent first, so a reconnect (or a join before CONNECT) restores this in onConnect.
    this.desiredConversations.add(conversationContextId);

    if (this.conversationSubscriptions.has(conversationContextId)) {
      console.log(`[STOMP] Already subscribed to conversation ${conversationContextId}`);
      return;
    }

    if (!this.stompClient?.connected) {
      console.log(`[STOMP] Not connected yet — conversation ${conversationContextId} queued for onConnect`);
      return;
    }

    this.subscribeToConversation(conversationContextId);
  }

  /** Opens the two per-conversation subscriptions. Only call once the client is connected. */
  private subscribeToConversation(conversationContextId: string): void {
    if (this.conversationSubscriptions.has(conversationContextId)) {
      return;
    }

    const msgTopic   = `/topic/conversation/${conversationContextId}`;
    const stateTopic = `/topic/conversation/${conversationContextId}/state`;

    const onMessage = (message: IMessage) => {
      try {
        const parsed = JSON.parse(message.body);
        // No client-side conversationContextId filter needed — topic is already scoped
        this.messageSubject.next(parsed);
      } catch (err) {
        console.error('[STOMP] Failed to parse message:', err, message.body);
      }
    };

    const subs: StompSubscription[] = [];
    try {
      subs.push(this.stompClient.subscribe(msgTopic, onMessage));
      subs.push(this.stompClient.subscribe(stateTopic, onMessage));
      console.log(`[STOMP] Subscribed to ${msgTopic} and ${stateTopic}`);
    } catch (err) {
      console.error('[STOMP] Error subscribing to conversation topics:', err);
      return;
    }
    this.conversationSubscriptions.set(conversationContextId, subs);
  }

  /**
   * Unsubscribe from per-conversation STOMP topics when the conversation is closed.
   */
  leaveConversation(conversationContextId: string): void {
    this.desiredConversations.delete(conversationContextId);
    const subs = this.conversationSubscriptions.get(conversationContextId);
    if (!subs) {
      return;
    }
    subs.forEach((s) => {
      try {
        s.unsubscribe();
      } catch (err) {
        console.error('[STOMP] Error unsubscribing from conversation topic:', err);
      }
    });
    this.conversationSubscriptions.delete(conversationContextId);
    console.log(`[STOMP] Unsubscribed from conversation ${conversationContextId}`);
  }

  claimConversation?(conversationContextId: string, agentId: string): void {
    const claimRequest = {
      conversationContextId: conversationContextId,
      agentId: agentId
    };
    
    fetch(`${environment.apiURL}api/rsocket/claim-conversation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(claimRequest)
    })
    .then(response => response.json())
    .then(data => {
      console.log(`RSocket: Agent ${agentId} claimed conversation ${conversationContextId}`, data);
    })
    .catch(error => {
      console.error('Error claiming conversation:', error);
    });
  }

  releaseConversation?(conversationContextId: string, agentId: string): void {
    const releaseRequest = {
      conversationContextId: conversationContextId,
      humanAgentId: agentId
    };
    
    fetch(`${environment.apiURL}api/rsocket/release-conversation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(releaseRequest)
    })
    .then(response => response.json())
    .then(data => {
      console.log(`RSocket: Agent ${agentId} released conversation ${conversationContextId}`, data);
    })
    .catch(error => {
      console.error('Error releasing conversation:', error);
    });
  }

  sendTypingIndicator?(conversationContextId: string, isTyping: boolean): void {
    if (this.stompClient && this.stompClient.connected) {
      const typingMessage = {
        conversationContextId: conversationContextId,
        messageSource: 'TYPING_INDICATOR',
        message: isTyping ? 'typing...' : '',
        agentName: ''
      };
      
      this.stompClient.publish({
        destination: '/app/chat.message',
        body: JSON.stringify(typingMessage)
      });
    }
  }
}
